/**
 * Error class hierarchy for query-layer + edge function failures.
 *
 * Discriminates the failure mode so the UI can render the right message
 * (and the right action) instead of a generic "Failed to load." Pre-2026
 * every async caller did `try/catch` and `toast.error('Failed to ...')`,
 * which gave the user no information and no retry path.
 *
 * Caller pattern:
 *   try { await thingThatMightFail() }
 *   catch (err) {
 *     if (err instanceof RateLimitError) { … upgrade prompt … }
 *     else if (err instanceof AuthExpiredError) { … redirect to /login … }
 *     else if (err instanceof NetworkError) { … inline retry … }
 *     else { … generic ErrorState … }
 *   }
 *
 * Or use the shared `<ErrorState />` which switches on these types
 * automatically. See `src/components/shared/ErrorState.jsx`.
 */

/** Network-layer failure — fetch threw before getting a response.
 *  Covers: offline, DNS lookup failure, TLS handshake, connection reset,
 *  CORS rejection. The user can usually recover by retrying. */
export class NetworkError extends Error {
  constructor(message = 'Network request failed', { cause } = {}) {
    super(message)
    this.name = 'NetworkError'
    if (cause) this.cause = cause
  }
}

/** Rate-limit hit — the server told us to slow down or the user has hit
 *  their plan cap. Triggers the upgrade prompt, not the retry button. */
export class RateLimitError extends Error {
  constructor(message = 'Rate limit reached', { reason, used, limit } = {}) {
    super(message)
    this.name = 'RateLimitError'
    this.reason = reason || null // e.g. 'monthly_messages' | 'rate_limit'
    this.used = used ?? null
    this.limit = limit ?? null
  }
}

/** Auth token expired or invalid — the call returned 401. The user
 *  should be sent back to /login. Don't auto-retry; that just compounds. */
export class AuthExpiredError extends Error {
  constructor(message = 'Your session has expired') {
    super(message)
    this.name = 'AuthExpiredError'
  }
}

/** Server-side failure (5xx) — the call reached the server but it
 *  errored. The user can retry; if it keeps failing, that's an outage. */
export class ServerError extends Error {
  constructor(message = 'Something went wrong on our end', { status, cause } = {}) {
    super(message)
    this.name = 'ServerError'
    this.status = status ?? null
    if (cause) this.cause = cause
  }
}

/** Discriminate a Response + parsed body into the right error class.
 *  Returns null if the response is OK (caller should not call this
 *  on a successful response). Used by `_edge.js` and `_helpers.js`. */
export function classifyResponseError(res, body, fallbackMessage) {
  const status = res?.status ?? 0
  const message = body?.error || body?.message || fallbackMessage || `Request failed: ${status}`

  if (status === 401 || status === 403) {
    return new AuthExpiredError(message)
  }
  if (status === 429 || body?.error === 'message_limit_reached') {
    return new RateLimitError(message, {
      reason: body?.error === 'message_limit_reached' ? 'monthly_messages' : 'rate_limit',
      used: body?.used ?? null,
      limit: body?.limit ?? null,
    })
  }
  if (status >= 500 && status < 600) {
    return new ServerError(message, { status })
  }
  // 4xx that isn't 401/403/429 — bad input, not retryable, surface the message
  return new Error(message)
}

/** Wrap a fetch-thrown TypeError into NetworkError so callers can
 *  branch consistently instead of duck-typing. Pass through anything
 *  else unchanged (already an Error subclass). */
export function wrapFetchError(err) {
  if (err instanceof Error && err.name === 'TypeError') {
    return new NetworkError(err.message || 'Network request failed', { cause: err })
  }
  return err
}

/** Map an error instance to user-friendly copy. Centralizes the wording
 *  so we change it in one place. Used by both `<ErrorState />` (inline
 *  block UI) and toast-with-retry callers (chat send, save note, save
 *  highlight) so error copy stays consistent across surfaces. */
export function friendlyErrorMessage(error) {
  if (!error) return 'Something went wrong.'
  switch (error.name) {
    case 'NetworkError':
      return "You're offline or the connection dropped — try again in a moment."
    case 'RateLimitError':
      return error.reason === 'monthly_messages'
        ? "You've reached your monthly message limit. Upgrade or wait until next month."
        : 'Slow down for a moment — too many requests at once.'
    case 'AuthExpiredError':
      return 'Your session expired. Sign in again to continue.'
    case 'ServerError':
      return "Something went wrong on our end. We've been notified — try again in a moment."
    default:
      return error.message || 'Something went wrong. Try again, or refresh the page.'
  }
}
